import { motion } from "framer-motion";
import { Wallet, Flame, Users, Droplets, Info } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import { useLanguage } from "@/i18n/LanguageContext";
import { projectConfig, effectiveShare } from "@/config/project";

const ICONS = [Wallet, Flame, Users, Droplets];
const SHARES = [
  projectConfig.taxDistribution.fundsRecipient,
  projectConfig.taxDistribution.burn,
  projectConfig.taxDistribution.holders,
  projectConfig.taxDistribution.liquidity,
];
const TEST_IDS = ["tax-dist-funds-card", "tax-dist-burn-card", "tax-dist-holders-card", "tax-dist-liquidity-card"];

export default function Tokenomics() {
  const { t } = useLanguage();
  const items = t("tokenomics.items");

  return (
    <section id="tokenomics" className="scroll-mt-24 bg-[#0D0608] px-4 py-24 sm:px-6 lg:px-8" data-testid="tokenomics-section">
      <div className="mx-auto max-w-7xl">
        <SectionHeader kicker={t("tokenomics.kicker")} title={t("tokenomics.title")} id="tokenomics" />

        {/* tax cards */}
        <div className="mb-14 grid gap-6 sm:grid-cols-2">
          {[
            { label: t("tokenomics.buyTax"), value: projectConfig.buyTax, id: "buy-tax-card" },
            { label: t("tokenomics.sellTax"), value: projectConfig.sellTax, id: "sell-tax-card" },
          ].map((c, i) => (
            <Reveal key={c.id} delay={0.08 * i}>
              <div
                data-testid={c.id}
                className="relative overflow-hidden rounded-2xl border border-red-900/40 bg-[#140A0D] p-8 text-center"
              >
                <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent" />
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">{c.label}</p>
                <p className="mt-3 font-display text-6xl font-black text-zinc-50 sm:text-7xl">
                  {c.value}
                  <span className="text-red-500">%</span>
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-500">{t("tokenomics.perTx")}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* explanation banner */}
        <Reveal>
          <div className="mb-14 flex gap-4 rounded-xl border border-yellow-700/40 bg-yellow-950/10 p-6 sm:p-8" data-testid="tax-explanation-banner">
            <Info size={22} className="mt-1 shrink-0 text-yellow-500" aria-hidden="true" />
            <div>
              <h3 className="mb-2 text-base font-bold text-yellow-400 sm:text-lg">{t("tokenomics.bannerTitle")}</h3>
              <p className="text-sm leading-relaxed text-zinc-300 sm:text-base">{t("tokenomics.bannerBody")}</p>
            </div>
          </div>
        </Reveal>

        {/* distribution bars */}
        <Reveal>
          <h3 className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-red-500">{t("tokenomics.distTitle")}</h3>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-2">
          {items.map((item, i) => {
            const Icon = ICONS[i];
            const share = SHARES[i];
            return (
              <Reveal key={item.name} delay={0.06 * i}>
                <div data-testid={TEST_IDS[i]} className="rounded-xl border border-red-900/30 bg-[#140A0D] p-6">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-950/60 text-red-400">
                        <Icon size={19} aria-hidden="true" />
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-zinc-100 sm:text-base">{item.name}</h4>
                        <p className="text-xs text-zinc-500">{item.desc}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-2xl font-black text-yellow-400">{share}%</p>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{t("tokenomics.shareOfTax")}</p>
                    </div>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-zinc-900" role="img" aria-label={`${item.name}: ${share}%`}>
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-red-700 via-red-500 to-yellow-500"
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                      style={{ width: `${share}%`, transformOrigin: "left" }}
                    />
                  </div>
                  <p className="mt-3 font-mono text-xs text-zinc-500">
                    ≈ <span className="text-red-400">{effectiveShare(share)}%</span> {t("tokenomics.perTx")}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 text-xs leading-relaxed text-zinc-500" data-testid="tokenomics-note">
            {t("tokenomics.note")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
